import React from "react";
import { Button, Textarea } from "@flixbus/honeycomb-react";
import {
  Icon,
  IconUpload,
  IconArrowRight,
} from "@flixbus/honeycomb-icons-react";
import { composerPlaceholder, maxNoteLength, maxFiles } from "./chatCopy";
import ComposerChips, { type ComposerFile } from "./ComposerChips.component";
import styles from "./ChatComposer.module.scss";

export type { ComposerFile };

export interface ChatComposerProps {
  note: string;
  files: ComposerFile[];
  disabled: boolean;
  canSend: boolean;
  onNoteChange: (value: string) => void;
  onFilesPicked: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  onSend: () => void;
}

/** Bottom composer: attached-file chips, upload button, multiline prompt field,
 *  send button. Disabled while a draft is generating. */
const ChatComposer: React.FC<ChatComposerProps> = ({
  note,
  files,
  disabled,
  canSend,
  onNoteChange,
  onFilesPicked,
  onRemoveFile,
  onSend,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const atFileLimit = files.length >= maxFiles;
  const noteOverLimit = note.length > maxNoteLength;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > 0) onFilesPicked(picked);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend && !disabled) onSend();
    }
  };

  return (
    <div className={styles.composer}>
      {files.length > 0 && (
        <ComposerChips
          files={files}
          disabled={disabled}
          onRemoveFile={onRemoveFile}
        />
      )}

      <Textarea
        id="ai-chat-composer"
        aria-label="Message"
        name="aiChatComposer"
        placeholder={composerPlaceholder}
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        maxLength={maxNoteLength}
        valid={noteOverLimit ? false : undefined}
        infoError={
          noteOverLimit ? `Maximum ${maxNoteLength} characters` : undefined
        }
        extraClasses={styles.composerInput}
      />

      <div className={styles.composerActions}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
          disabled={disabled || atFileLimit}
          aria-label="Upload PDF documents"
        />
        <Button
          appearance="secondary"
          display="square"
          disabled={disabled || atFileLimit}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Upload a document"
        >
          <Icon InlineIcon={IconUpload} aria-hidden="true" />
        </Button>
        <Button
          appearance="primary"
          display="square"
          disabled={!canSend || disabled}
          onClick={onSend}
          aria-label="Send"
        >
          <Icon InlineIcon={IconArrowRight} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

export default React.memo(ChatComposer);
